import React, { useEffect, useState } from 'react';
import '../commandPanel.scss';
import { isNullOrUndefined } from 'utils/Global';
import { PlateInfoDTO, PlateSeedInfoDTO, SavePlateInfoDto } from 'business/models/Models';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import { selectExp } from 'redux/selectors/ExperimentStateSelector';
import { setExperiment } from 'redux/reducers/experimentDataReducer';
import useRMLite from 'CustomHooks/useRMLite';
import { useLastSelectedWellNavigationDetails } from 'redux/hooks/experiment';
import {
  formatLocalDateTimeInputStr,
  shortDayMonthYearTimeFormat,
} from 'components/main/Hooks/ExperimentCommands/utils';
import { EPlateState } from 'enums/EPlatestate';
import PlateService from 'business/services/PlateService';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import { PLATE_SEEDS_CHANGED_EVENT } from './plateSeedsEvents';
import DateHelper from 'utils/DateHelper';
import { EPlateWellStates } from 'enums/EPlateWellStates';
import { renderPlateChip } from 'utils/PlateUtils';

export type plateInfoProps = {
  label: string;
  text: any;
  type?: string;
  disabled?: boolean;
  onChange?: (val) => void;
};
let updatedPlateDispensed: SavePlateInfoDto[] = [];

function PlateSeedCard({ seed, index }: { seed: PlateSeedInfoDTO; index: number }) {
  return (
    <div
      id={`plateinfo-seed-card-${index}`}
      className="flex items-start gap-2.5 border border-[#cfcfcf] rounded-md px-3 py-2.5 bg-white [&_svg_path]:fill-[#555555] h-[12%]"
    >
      <RMSvgIcon
        id={`plateinfo-seed-icon-${index}`}
        name="Seed"
        width={20}
        height={20}
        fill="#555555"
      />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold text-[#222] leading-snug">
          {seed.name || 'Seed'}
        </div>
        <div className="mt-0.5 text-xs text-[#777] leading-snug">
          Well {seed.wellName || '-'} / Drop {seed.dropNumber}
        </div>
      </div>
      <div className="ml-2 text-xs text-[#9a9a9a] whitespace-nowrap">
        {seed.dateCreated ? DateHelper.formatDate(seed.dateCreated) : ''}
      </div>
    </div>
  );
}

function PlateInfoRow({ label, text, type, onChange, disabled = false }: plateInfoProps) {
  const [value, setValue] = useState(getFormattedValue(text));

  function getFormattedValue(input) {
    if (type === 'date' && input) {
      try {
        return formatLocalDateTimeInputStr(new Date(input));
      } catch (error) {
        console.warn('provided date is invalid', { input, error });
      }
    }
    return '';
  }

  function onChangeField(e: React.ChangeEvent<HTMLInputElement>) {
    const newVal = e.target.value;
    setValue(newVal);
    if (onChange) {
      onChange(newVal);
    }
  }

  return (
    <div id="plateinfo-wrapper" className="d-flex items-center h-[4%]">
      <div id={`plateinfo-label-${label}`} className="Plate-Info-Row-Label">
        {label}
      </div>
      {type === 'date' ? (
        <input
          className="Plate-Info-Row-Text min-w-min"
          onChange={onChangeField}
          value={value}
          type="datetime-local"
          id="plateinfo-plateDispensed"
          disabled={disabled}
        />
      ) : (
        <div id={`plateinfo-${text}`} className="Plate-Info-Row-Text">
          {text}
        </div>
      )}
    </div>
  );
}
export default function PlateInfo() {
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const [pInfo, setPlateInfo] = useState<PlateInfoDTO>();
  const exp = useRMSelector(selectExp);
  const dispatch = useRMDispatch();
  const rmLite = useRMLite();
  const { plateID } = wellNavigationDetails || {};
  function updateDispensedDates(dispensedDate: string, pId: number) {
    const filteredDispensedDate = updatedPlateDispensed.filter((pd) => pd.id !== pId);
    if (filteredDispensedDate.length === 0) {
      updatedPlateDispensed = [
        { id: pId, dispensedDate: new Date(dispensedDate) } as SavePlateInfoDto,
      ];
    } else {
      updatedPlateDispensed = [
        ...updatedPlateDispensed,
        { id: pId, dispensedDate: new Date(dispensedDate) } as SavePlateInfoDto,
      ];
    }
    if (!isNullOrUndefined(exp)) {
      dispatch(
        setExperiment({ plateDispensedDetails: updatedPlateDispensed, isDirty: true }),
      );
    }
  }
  useEffect(() => {
    const getDefaultPlateInfo = () =>
      new PlateInfoDTO({
        id: 0,
        name: exp?.expName,
        dispensed: '',
        imaged: '',
        robotName: '',
        plateState: EPlateState.Designed,
        hasSeedLayer: false,
        plateNumber: 0,
        isExpired: false,
        wellState: EPlateWellStates.Designed,
      });
    async function fetchPlateInfo() {
      if (!plateID) {
        setPlateInfo(getDefaultPlateInfo());
        return;
      }
      let data = await new PlateService().getPlateInfo(false, plateID);
      if (data == null) {
        data = getDefaultPlateInfo();
      }
      setPlateInfo(data);
    }
    fetchPlateInfo();

    const onSeedsChanged = (e: Event) => {
      const plateId = (e as CustomEvent<{ plateId?: number }>).detail?.plateId;
      if (plateId != null && plateId !== plateID) {
        return;
      }
      fetchPlateInfo();
    };
    window.addEventListener(PLATE_SEEDS_CHANGED_EVENT, onSeedsChanged);
    return () => window.removeEventListener(PLATE_SEEDS_CHANGED_EVENT, onSeedsChanged);
  }, [plateID]);
  function onChangeDispensedDate(val) {
    updateDispensedDates(val, pInfo.id);
  }

  return (
    <>
      {pInfo && (
        <div id="plateinforow-container" className="h-[100%]">
          <PlateInfoRow label="ID" text={pInfo.id} />
          <PlateInfoRow label="Name" text={pInfo.name} />
          <PlateInfoRow
            label="Dispensed"
            text={pInfo.dispensed}
            type="date"
            onChange={onChangeDispensedDate}
            disabled={
              window.location.pathname.includes('/screen') || !pInfo.dispensed || rmLite
            }
          />
          <PlateInfoRow
            label="Imaged"
            text={
              pInfo.imaged
                ? new Date(pInfo.imaged).toLocaleTimeString(
                    'en-US',
                    shortDayMonthYearTimeFormat,
                  )
                : ''
            }
          />
          <PlateInfoRow
            label="Location"
            text={
              pInfo.robotName ? pInfo.robotName : <i>This plate is not in the imager.</i>
            }
          />
          <PlateInfoRow
            label="Status"
            text={<div className="Plate-Info-Chip">{renderPlateChip(pInfo)}</div>}
          />
          <div id="plateinfo-seeds-section" className="mx-3 mt-3 mb-2 ml-[3%] h-[75%]">
            <div
              id="plateinfo-seeds-label"
              className="text-[13px] font-semibold text-black mb-2 h-[4%]"
            >
              Seeds
            </div>
            {pInfo.seeds?.length ? (
              <div
                id="plateinfo-seeds-list"
                className="flex flex-col gap-2 h-[94%] overflow-y-auto"
              >
                {pInfo.seeds.map((seed, index) => (
                  <PlateSeedCard
                    key={`${seed.wellName}-${seed.dropNumber}-${index}`}
                    seed={seed}
                    index={index}
                  />
                ))}
              </div>
            ) : (
              <div
                id="plateinfo-seeds-empty"
                className="text-[13px] text-[#b0b0b0] leading-snug"
              >
                This plate is not seeded
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
